import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import {
  ClinicalPageShell,
  SignalBar,
  ClinicalDataGrid,
  StatusPill,
  CodeChip,
  tokens,
} from "../../shared/clinical-design";
import { APPT_STATUS, APPT_TYPES, NOTE_STATUS, CHARGE_STATUS, CDCI_BASE_PATH } from "../common/constants";
import * as cdci from "../services/cdciAmdService";

/**
 * CDCI 4.1 — My Schedule (página principal del médico, Paso 1 del journey).
 * El sistema identifica SU location y muestra SUS citas del día con estado
 * de nota y cargos por cita. Fuente futura: GET api/amd/appointments.
 */
export default function CdciSchedulePage() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [providerId, setProviderId] = useState(101);
  const [locationId, setLocationId] = useState(1);
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [appointments, setAppointments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cdci.getProviders().then(setProviders);
    cdci.getLocations().then(setLocations);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [appts, sum] = await Promise.all([
        cdci.getAppointments({ providerId, locationId, date }),
        cdci.getDaySummary({ providerId, locationId }),
      ]);
      setAppointments(appts);
      setSummary(sum);
    } finally {
      setLoading(false);
    }
  }, [providerId, locationId, date]);

  useEffect(() => {
    load();
  }, [load]);

  const signals = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Appointments", value: summary.active, hint: "Active appointments today (excludes cancelled / no-show)" },
      { label: "Completed", value: summary.completed, tone: "ok" },
      {
        label: "Notes pending",
        value: summary.notesPending,
        tone: summary.notesPending > 0 ? "warn" : "ok",
        hint: "Encounters whose note is not signed yet",
      },
      {
        label: "Charges pending",
        value: summary.chargesPending,
        tone: summary.chargesPending > 0 ? "warn" : "ok",
        hint: "Encounters without captured charges",
      },
      {
        label: "Completed w/o charges",
        value: summary.unbilledCompleted,
        tone: summary.unbilledCompleted > 0 ? "crit" : "ok",
        hint: "Revenue at risk: completed visits with no charges captured",
      },
    ];
  }, [summary]);

  const columns = [
    {
      key: "time",
      header: "Time",
      width: 70,
      mono: true,
      render: (r) => r.time,
    },
    {
      key: "type",
      header: "Type",
      width: 150,
      render: (r) => {
        const t = APPT_TYPES[r.typeKey];
        return (
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: t?.color || tokens.muted }} />
            <Typography sx={{ fontSize: 13 }}>{t?.label || r.typeKey}</Typography>
          </Stack>
        );
      },
    },
    {
      key: "patient",
      header: "Patient",
      render: (r) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{r.patient?.name}</Typography>
          <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>
            {r.patient?.age}y · {r.patient?.gender}
          </Typography>
        </Stack>
      ),
    },
    {
      key: "cipId",
      header: "CIP ID",
      width: 130,
      render: (r) => <CodeChip code={String(r.patient?.cipId || "")} />,
    },
    {
      key: "status",
      header: "Appt status",
      width: 120,
      render: (r) => {
        const s = APPT_STATUS[r.statusKey] || APPT_STATUS.SCHEDULED;
        return <StatusPill tone={s.tone} label={s.label} dense />;
      },
    },
    {
      key: "noteStatus",
      header: "Note",
      width: 110,
      render: (r) => {
        const s = NOTE_STATUS[r.noteStatus] || NOTE_STATUS.NOT_STARTED;
        return <StatusPill tone={s.tone} label={s.label} dense />;
      },
    },
    {
      key: "chargeStatus",
      header: "Charges",
      width: 120,
      render: (r) => {
        const s = CHARGE_STATUS[r.chargeStatus] || CHARGE_STATUS.NOT_CAPTURED;
        return <StatusPill tone={s.tone} label={s.label} dense />;
      },
    },
  ];

  return (
    <ClinicalPageShell
      title="My Schedule"
      subtitle="Your appointments for the day, with documentation and charge status per encounter. Open an appointment to review the patient context and start the note."
      moduleTag="CDCI"
      devPreview
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            type="date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            sx={{ bgcolor: tokens.surface, width: 160 }}
          />
          <Select
            size="small"
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
            sx={{ bgcolor: tokens.surface, minWidth: 190 }}
          >
            {providers.map((p) => (
              <MenuItem key={p.providerId} value={p.providerId}>{p.name}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            sx={{ bgcolor: tokens.surface, minWidth: 200 }}
          >
            {locations.map((l) => (
              <MenuItem key={l.locationId} value={l.locationId}>{l.name}</MenuItem>
            ))}
          </Select>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} sx={{ border: `1px solid ${tokens.line}`, bgcolor: tokens.surface }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 2 }} />
      <ClinicalDataGrid
        columns={columns}
        rows={appointments}
        rowKey={(r) => r.appointmentId}
        loading={loading}
        onRowClick={(r) => navigate(`${CDCI_BASE_PATH}/encounter/${r.appointmentId}`)}
        emptyMessage="No appointments for this provider, location and date."
        maxHeight={640}
      />
    </ClinicalPageShell>
  );
}
